import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Microscope, Users, Wallet, TrendingUp } from "lucide-react";
import TopBar from "../components/TopBar";
import BenchmarkStrip from "../components/BenchmarkStrip";
import ClientCreateDialog from "../components/ClientCreateDialog";
import BulkTradeDialog from "../components/BulkTradeDialog";
import AlertsFeed from "../components/AlertsFeed";
import { apiClient } from "../lib/api";

export default function AdvisorDashboard() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/advisor/clients");
      setClients(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalAum = clients.reduce((s, c) => s + Number(c.initial_capital || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <TopBar subtitle="Advisor Console" />
      <main className="px-4 sm:px-6 lg:px-10 py-5 sm:py-8 max-w-[1600px] mx-auto">
        <div className="mb-8">
          <BenchmarkStrip />
        </div>

        {/* Hero strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <div className="card-noir p-5 rounded-lg">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">Clients</div>
            <div className="font-display text-3xl font-extrabold mt-2 tabular tracking-tighter flex items-center gap-2">
              <Users className="h-5 w-5 text-lime" strokeWidth={2} />
              {clients.length.toString().padStart(2, "0")}
            </div>
          </div>
          <div className="card-noir p-5 rounded-lg">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">Initial AUM</div>
            <div className="font-display text-3xl font-extrabold mt-2 tabular tracking-tighter">
              <span className="text-muted-foreground text-xl align-top mr-0.5">₹</span>
              {(totalAum / 1e7).toFixed(2)}<span className="text-muted-foreground text-base ml-1">Cr</span>
            </div>
          </div>
          <div className="card-noir p-5 rounded-lg">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">Active</div>
            <div className="font-display text-3xl font-extrabold mt-2 tabular tracking-tighter flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-profit" strokeWidth={2} />
              {clients.length}
            </div>
          </div>
          <div className="card-noir p-5 rounded-lg">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">Status</div>
            <div className="font-display text-base font-bold mt-2 inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-lime lime-pulse" />
              <span className="text-lime">Operational</span>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <AlertsFeed />
        </div>

        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[10px] text-muted-foreground tracking-[0.28em] uppercase font-mono font-semibold">
              The Book
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-1 tracking-tighter">
              Your <span className="text-lime">clients</span>.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/advisor/research"
              data-testid="research-desk-btn"
              className="border border-border hover:border-lime hover:text-lime px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] rounded-md flex items-center gap-1.5 font-mono transition-colors"
            >
              <Microscope className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">Research Desk</span>
              <span className="sm:hidden">Research</span>
            </Link>
            <BulkTradeDialog clients={clients} onCreated={load} />
            <ClientCreateDialog onCreated={load} />
          </div>
        </div>

        {loading ? (
          <div className="card-noir p-12 text-sm text-muted-foreground text-center rounded-lg">
            Loading clients…
          </div>
        ) : clients.length === 0 ? (
          <div className="card-noir p-16 text-center rounded-lg">
            <Wallet className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-display text-xl font-bold mb-1">No clients yet</div>
            <div className="text-sm text-muted-foreground">
              Create your first client to start tracking their equity portfolio.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {clients.map((c) => (
              <Link
                key={c.id}
                to={`/advisor/clients/${c.id}`}
                data-testid={`client-card-${c.id}`}
                className="card-noir p-5 rounded-lg group block hover:-translate-y-[1px]"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold tracking-tight truncate">
                      {c.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate font-mono">
                      {c.email || <span className="italic">no email</span>}
                    </div>
                  </div>
                  <ArrowUpRight
                    className="h-4 w-4 text-muted-foreground/70 group-hover:text-lime shrink-0 transition-colors"
                    strokeWidth={2}
                  />
                </div>
                <div className="mt-4">
                  <code className="text-[11px] font-mono bg-secondary text-lime px-2 py-1 rounded">
                    {c.client_code}
                  </code>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-xs pt-4 border-t border-border">
                  <div>
                    <div className="text-[9px] text-muted-foreground uppercase tracking-[0.2em] font-mono">
                      Start
                    </div>
                    <div className="font-mono mt-0.5 tabular text-foreground">{c.start_date}</div>
                  </div>
                  <div>
                    <div className="text-[9px] text-muted-foreground uppercase tracking-[0.2em] font-mono">
                      Capital
                    </div>
                    <div className="font-mono mt-0.5 tabular text-foreground">
                      ₹{Number(c.initial_capital || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] text-muted-foreground uppercase tracking-[0.2em] font-mono">
                      Fee
                    </div>
                    <div className="font-mono mt-0.5 tabular text-lime">
                      {c.annual_fee_pct ? `${c.annual_fee_pct}%` : "—"}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
